import { useEffect, useState, Fragment } from "react";
import api, { inr } from "../../lib/api";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Package } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";

const STATUSES = ["pending", "confirmed", "shipped", "delivered", "cancelled"];

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [openId, setOpenId] = useState(null);
  const load = () => api.get("/admin/orders").then((r) => setOrders(r.data));
  useEffect(() => { load(); }, []);

  const updateStatus = async (order_id, status) => {
    await api.put(`/admin/orders/${order_id}`, { status });
    toast.success("Order status updated");
    load();
  };

  const toggle = (id) => setOpenId((cur) => (cur === id ? null : id));

  return (
    <div data-testid="admin-orders">
      <h1 className="font-serif text-4xl tracking-tight mb-1">Orders</h1>
      <p className="text-ink-soft text-sm mb-8">{orders.length} total orders · click a row to see what was ordered</p>

      <div className="border border-line rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-black/[0.03] text-left">
            <tr className="overline text-ink-soft">
              <th className="p-4 font-medium">Order</th>
              <th className="p-4 font-medium">Customer</th>
              <th className="p-4 font-medium">Items</th>
              <th className="p-4 font-medium">Total</th>
              <th className="p-4 font-medium">Payment</th>
              <th className="p-4 font-medium">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {orders.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-ink-soft">No orders yet.</td></tr>}
            {orders.map((o) => {
              const isOpen = openId === o.order_id;
              return (
                <Fragment key={o.order_id}>
                  <tr
                    key={o.order_id}
                    data-testid={`admin-order-${o.order_id}`}
                    className="cursor-pointer hover:bg-black/[0.015]"
                    onClick={() => toggle(o.order_id)}
                  >
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        {isOpen ? <ChevronDown size={14} className="text-ink-soft" /> : <ChevronRight size={14} className="text-ink-soft" />}
                        <div>
                          <p className="mono text-xs">#{o.order_id.slice(0, 8).toUpperCase()}</p>
                          <p className="text-ink-soft text-xs mt-1">{new Date(o.created_at).toLocaleDateString("en-IN")}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <p>{o.user_name}</p>
                      <p className="text-ink-soft text-xs">{o.user_email}</p>
                    </td>
                    <td className="p-4 text-ink-soft" data-testid={`order-items-count-${o.order_id}`}>{o.items.length} item{o.items.length !== 1 ? "s" : ""}</td>
                    <td className="p-4">{inr(o.total)}</td>
                    <td className="p-4">
                      <span className={`overline ${o.payment_status === "paid" ? "text-green-700" : "text-ink-soft"}`}>{o.payment_status}</span>
                    </td>
                    <td className="p-4" onClick={(e) => e.stopPropagation()}>
                      <Select value={o.status} onValueChange={(v) => updateStatus(o.order_id, v)}>
                        <SelectTrigger className="w-36 h-9" data-testid={`order-status-${o.order_id}`}><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-white">{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr key={`${o.order_id}-detail`} className="bg-[#faf8f4]" data-testid={`order-detail-${o.order_id}`}>
                      <td colSpan={6} className="p-6">
                        <div className="grid md:grid-cols-[1fr_280px] gap-8">
                          <div>
                            <p className="overline text-ink-soft mb-3 flex items-center gap-2"><Package size={13} /> Items in this order</p>
                            <div className="space-y-3">
                              {o.items.map((it, i) => (
                                <div key={i} className="flex items-center gap-4" data-testid={`order-line-${o.order_id}-${i}`}>
                                  {it.image
                                    ? <img src={it.image} alt="" className="h-16 w-14 object-cover bg-[#efece6] rounded" />
                                    : <div className="h-16 w-14 bg-[#efece6] rounded" />}
                                  <div className="flex-1 min-w-0">
                                    <p className="font-serif text-base leading-tight">{it.name}</p>
                                    <p className="text-ink-soft text-xs mt-1">
                                      Size {it.size || "—"} · Qty {it.quantity} · {inr(it.price)} each
                                    </p>
                                  </div>
                                  <p className="text-sm">{inr(it.price * it.quantity)}</p>
                                </div>
                              ))}
                            </div>
                            <div className="mt-4 pt-3 border-t border-line flex flex-col gap-1 text-sm max-w-xs ml-auto">
                              <div className="flex justify-between text-ink-soft"><span>Subtotal</span><span>{inr(o.subtotal ?? o.total)}</span></div>
                              {o.discount > 0 && <div className="flex justify-between text-ink-soft"><span>Discount{o.coupon_code ? ` (${o.coupon_code})` : ""}</span><span>− {inr(o.discount)}</span></div>}
                              <div className="flex justify-between font-medium pt-1 border-t border-line mt-1"><span>Total</span><span>{inr(o.total)}</span></div>
                            </div>
                          </div>
                          <div>
                            <p className="overline text-ink-soft mb-3">Shipping to</p>
                            {o.shipping && Object.keys(o.shipping).length > 0 ? (
                              <div className="text-sm text-ink leading-relaxed">
                                {["name", "fullName", "address", "line1", "line2", "city", "state", "pincode", "zip", "country", "phone", "email"]
                                  .filter((k) => o.shipping[k])
                                  .map((k) => <p key={k}>{o.shipping[k]}</p>)}
                                {/* Fallback: dump any unknown fields */}
                                {Object.entries(o.shipping)
                                  .filter(([k, v]) => v && !["name", "fullName", "address", "line1", "line2", "city", "state", "pincode", "zip", "country", "phone", "email"].includes(k))
                                  .map(([k, v]) => <p key={k} className="text-ink-soft"><span className="capitalize">{k}</span>: {String(v)}</p>)}
                              </div>
                            ) : (
                              <p className="text-ink-soft text-sm italic">No shipping details.</p>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
